import { useMemo } from "react";
import { Activity, AlertTriangle, Crosshair } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getBehaviorForecast,
  severityBand,
  type ForecastResponseMode,
} from "@/lib/forecastEngine";

interface BehavioralForecastPanelProps {
  tactic: string | null | undefined;
  archetype?: string | null;
  responseMode: ForecastResponseMode;
  /** "full" = inside neutralizer card; "compact" = embed in action card */
  variant?: "full" | "compact";
  className?: string;
}

const bandClasses: Record<"ok" | "warn" | "alert", { bar: string; text: string; dot: string }> = {
  ok:    { bar: "bg-emerald-400",  text: "text-emerald-400",  dot: "bg-emerald-400" },
  warn:  { bar: "bg-amber-400",    text: "text-amber-400",    dot: "bg-amber-400" },
  alert: { bar: "bg-red-500",      text: "text-red-500",      dot: "bg-red-500" },
};

function MetricRow({
  label,
  value,
  inverted = false,
  suffix = "%",
}: {
  label: string;
  value: number;
  inverted?: boolean;
  suffix?: string;
}) {
  const band = severityBand(value, inverted);
  const cls = bandClasses[band];
  return (
    <div className="flex items-center gap-2 leading-none">
      <span className="text-[9.5px] uppercase tracking-[0.16em] font-mono text-muted-foreground/70 w-[112px] shrink-0">
        {label}
      </span>
      <div className="flex-1 h-[3px] bg-background/70 rounded-full overflow-hidden border border-border/40">
        <div
          className={cn("h-full transition-[width] duration-500 ease-out", cls.bar)}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className={cn("text-[10.5px] font-mono tabular-nums w-[34px] text-right", cls.text)}>
        {value}{suffix}
      </span>
    </div>
  );
}

const BehavioralForecastPanel = ({
  tactic,
  archetype,
  responseMode,
  variant = "full",
  className,
}: BehavioralForecastPanelProps) => {
  const forecast = useMemo(
    () => getBehaviorForecast({ tactic, archetype, responseMode }),
    [tactic, archetype, responseMode],
  );

  const escBand = severityBand(forecast.escalationRisk);
  const stabilityBand = severityBand(forecast.stabilityScore, true);
  const headerCls = bandClasses[escBand];

  return (
    <div
      key={responseMode}
      className={cn(
        "relative rounded-lg border border-border/80 bg-background/40 overflow-hidden",
        "animate-in fade-in duration-300",
        className,
      )}
    >
      {/* signal pulse strip */}
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <div
          className={cn("h-full w-full opacity-70", headerCls.bar)}
          style={{ animation: "pulse 2.4s ease-in-out infinite" }}
        />
      </div>

      {/* header */}
      <div className="flex items-center justify-between px-3 pt-2 pb-1.5">
        <div className="flex items-center gap-1.5">
          <Activity className={cn("w-3 h-3", headerCls.text)} />
          <span className="text-[9.5px] uppercase tracking-[0.2em] font-mono font-semibold text-foreground/80">
            What's likely to happen next
          </span>

        </div>
        <span className={cn("inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider", headerCls.text)}>
          <span className={cn("w-1 h-1 rounded-full", headerCls.dot, "animate-pulse")} />
          Live
        </span>
      </div>

      <div className={cn("px-3 pb-2.5 space-y-1.5", variant === "compact" && "pb-2 space-y-1")}>
        <MetricRow label="Escalation Risk"   value={forecast.escalationRisk} />
        <MetricRow label="Withdrawal"        value={forecast.withdrawalProbability} />
        <MetricRow label="Accountability"    value={forecast.accountabilityProbability} inverted />
        {variant === "full" && (
          <MetricRow label="Stability"       value={forecast.stabilityScore} inverted />
        )}

        {/* Countermove */}
        <div className="flex items-center gap-2 pt-1.5 mt-1 border-t border-border/40">
          <span className="text-[9.5px] uppercase tracking-[0.16em] font-mono text-muted-foreground/70 w-[112px] shrink-0">
            Their likely move
          </span>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Crosshair className="w-2.5 h-2.5 text-cyan shrink-0" />
            <span className="text-[11px] text-foreground/90 font-mono truncate">
              {forecast.likelyCountermove}
            </span>
          </div>
        </div>
        <div className="pl-[120px] -mt-0.5">
          <p className="text-[9.5px] leading-snug text-muted-foreground/60 italic">
            → A calm, steady reply holds the line. See your reply options below.
          </p>
        </div>


        {variant === "full" && (
          <div className="flex items-start gap-1.5 pt-1.5 mt-1 border-t border-border/40">
            <AlertTriangle className={cn("w-2.5 h-2.5 mt-[2px] shrink-0", bandClasses[stabilityBand].text)} />
            <p className="text-[10.5px] leading-snug text-foreground/65 italic font-mono">
              {forecast.summary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BehavioralForecastPanel;
